import React from "react";
import {
  Box,
  Stack,
  Avatar,
  TextField,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/system";
import SendIcon from "@mui/icons-material/Send";
import { useApiManager } from "lib/ApiContext";

/**
 * CommentsPanel
 * Un único componente que maneja: composer + lista + paginación.
 *
 * Props:
 *  - publicationID: string | number (requerido)
 *  - autoFocus?: boolean (default false)
 *  - avatarUrl?: string (para el usuario actual)
 */

// ===== Estilos acorde a FullView (bg #201a20, bordes cian/morado) =====
const Panel = styled(Box)({
  backgroundColor: "#201a20",
  borderRadius: 8,
  border: "1px solid rgba(93,210,255,0.35)",
});

const ComposerField = styled(TextField)({
  "& .MuiInputBase-input, & textarea": {
    color: "#e8e8e8",
  },
  "& .MuiInput-underline:before, & .MuiInput-underline:after": {
    borderBottom: "1px solid rgba(93,210,255,0.65)",
  },
});

function CommentItem({ comment }) {
  const initial = (comment?.User?.charAt?.(0) || "?").toUpperCase();
  const dateStr = comment?.Date ? new Date(comment.Date).toLocaleString() : "";
  const api = useApiManager();
  const [userData, setUserData] = React.useState(null);

  const loadUserData = React.useCallback(async () => {
     const uData = await api.GetUserPublic(comment?.User);
     setUserData(uData);
  }, [comment]);

   React.useEffect(() => {
      loadUserData();
   },[comment]);

  return (
    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: "#6A00CC" }} src={userData?.SignedUrl || "/images/avatar-default.webp"}>{initial}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 600, color: "#bde3ff" }}>
              {userData?.UserName || "user"}
            </Typography>
            <Chip
              label={dateStr}
              size="small"
              sx={{
                bgcolor: "rgba(157,0,255,0.15)",
                color: "#a0c8ff",
                borderColor: "#5dd2ff",
              }}
              variant="outlined"
            />
          </Stack>
        }
        secondary={<Typography sx={{ color: "white", mt: 0.5 }}>{comment?.Comment}</Typography>}
      />
    </ListItem>
  );
}

export default function CommentsPanel({ publicationID, autoFocus = false, avatarDirty, avatarUrl, loggedIn, setShowLogin }) {
  const api = useApiManager();

  // composer state
  const [text, setText] = React.useState("");
  const [posting, setPosting] = React.useState(false);
  const [postError, setPostError] = React.useState("");

  // list state
  const [comments, setComments] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(false);
  const [lastKey, setLastKey] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const avatarRef = React.useRef(null);

  const maxLen = 1000;

  const loadComments = React.useCallback(
    async (reset = false) => {
      if (!api || !publicationID) return;
      if (loadingList) return;
      setLoadingList(true);
      try {
        const resp = await api.GetComments(
          String(publicationID),
          10,
          "desc",
          reset ? null : lastKey,
          null
        );
        const items = resp?.items || [];
        const nextKey = resp?.LastEvaluatedKey || resp?.lastKey || null;

        if (Array.isArray(items) && items.length > 0) {
          setComments((prev) => (reset ? [...items] : [...prev, ...items]));
          setLastKey(nextKey);
          setHasMore(Boolean(nextKey));
        } else if (reset) {
          setComments([]);
          setLastKey(null);
          setHasMore(false);
        }
      } catch (e) {
        console.error("Error loading comments", e);
      } finally {
        setLoadingList(false);
        setFirstLoad(false);
      }
    },
    [api, publicationID, lastKey]
  );

  React.useEffect(() => {
    // reset al cambiar publicación
    setComments([]);
    setLastKey(null);
    setHasMore(true);
    setFirstLoad(true);
    loadComments(true);
  }, [publicationID, loadComments]);

   React.useEffect(() => {
      if (avatarRef.current)
      {
        avatarRef.current.src = avatarUrl; 
      }
   }, loggedIn, avatarDirty);

  const handlePost = async () => {
    if (!loggedIn)
    {
      setShowLogin(true);
      setPosting(false);
      return;
    }
    setPostError("");
    const body = text.trim();
    if (!publicationID) return setPostError("Falta PublicationID.");
    if (!body) return setPostError("Escribe tu comentario.");

    const payload = {
      PublicationID: String(publicationID),
      Comment: body,
      // ReplyToID: sólo si se usa modo reply (no en este panel base)
    };

    try {
      setPosting(true);
      await api.PostComment(String(publicationID), payload);
      setText("");
      // recargar lista desde el inicio para ver el nuevo primero
      await loadComments(true);
    } catch (e) {
      console.error(e);
      setPostError("No se pudo publicar. Intenta de nuevo.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Panel sx={{ mt: 3, p: 2, mb: 4 }}>
      <Typography sx={{ color: "#a0c8ff", fontWeight: 700, mb: 1.5 }} variant="h6">
        Comentarios
      </Typography>
      <Divider sx={{ borderColor: "rgba(157,0,255,0.35)", mb: 1.5 }} />

      {/* Composer */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
        <Avatar ref={avatarRef} src={avatarUrl || "/images/avatar-default.webp"} alt="user" sx={{ width: 40, height: 40, bgcolor: "#6A00CC" }} />
        <Stack flex={1} spacing={1}>
          <ComposerField
            variant="standard"
            fullWidth
            multiline
            minRows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Deja un comentario…"
            autoFocus={autoFocus}
            slotProps={{ htmlInput: { maxLength: maxLen } }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: "#c9c9c9" }}>
              {text.length}/{maxLen}
            </Typography>
            <Button
              variant="outlined"
              onClick={handlePost}
              disabled={posting}
              endIcon={<SendIcon />}
              sx={{
                borderColor: "#5dd2ff",
                color: "#bde3ff",
                "&:hover": { borderColor: "#9d00ff" },
              }}
            >
              {posting ? "Publicando…" : "Comentar"}
            </Button>
          </Stack>
          {postError && (
            <Typography variant="caption" sx={{ color: "#ff6b6b" }}>
              {postError}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Lista */}
      {firstLoad && comments.length === 0 ? (
        <Stack spacing={1}>
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Stack>
      ) : (
        <>
          <List dense sx={{ width: "100%" }}>
            {comments.map((c) => (
              <CommentItem key={c.CommentID || `${c.User}-${c.Date}`} comment={c} />
            ))}
          </List>

          {comments.length === 0 && !loadingList && (
            <Typography sx={{ color: "#c9c9c9", py: 2 }}>
              Aún no hay comentarios. ¡Sé el primero en opinar!
            </Typography>
          )}

          {hasMore && (
            <Box sx={{ display: "grid", placeItems: "center", mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => loadComments(false)}
                disabled={loadingList}
                sx={{
                  borderColor: "#5dd2ff",
                  color: "#bde3ff",
                  "&:hover": { borderColor: "#9d00ff" },
                }}
              >
                {loadingList ? "Cargando…" : "Cargar más"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Panel>
  );
}
